import { useCallback, useEffect, useState } from 'react';

import type { SessionInfo } from '../net/api';
import { ApiError, fetchSession } from '../net/api';

export type SessionInfoState =
  | { status: 'loading' }
  | { status: 'ready'; info: SessionInfo }
  | { status: 'error'; error: ApiError };

/**
 * `GET /api/sessions/:id` before anything else: it tells us the session name, and
 * whether a passcode is needed, without spending a WebSocket connection on finding
 * out — and it is what lets the page say "no such session" instead of failing
 * mysteriously after the user has already pressed Listen.
 */
export function useSessionInfo(sessionId: string): {
  state: SessionInfoState;
  reload(): void;
} {
  const [state, setState] = useState<SessionInfoState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const abort = new AbortController();
    setState({ status: 'loading' });

    fetchSession(sessionId, abort.signal).then(
      (info) => {
        if (!abort.signal.aborted) setState({ status: 'ready', info });
      },
      (error: unknown) => {
        if (abort.signal.aborted) return;
        setState({
          status: 'error',
          error:
            error instanceof ApiError
              ? error
              : new ApiError('could not load this session', 0, 'offline'),
        });
      },
    );

    return () => abort.abort();
  }, [sessionId, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);
  return { state, reload };
}
